import { signOut } from "firebase/auth";

import { auth } from "~/lib/firebase";
import { logger } from "~/services/logger";
import { clearSentryTagProfile, setSentryTagProfile } from "~/utils/sentry";

import { authStore, INITIAL_STATE } from "./store";
import type { AuthStore } from "./store.types";

/**
 * Set user data after user login
 */
function login(
  params: Required<Pick<AuthStore, "partnerId" | "isKanplaAdmin">> & {
    token?: string;
  }
): void {
  authStore.setState({
    status: "user",
    partnerId: params.partnerId,
    isKanplaAdmin: params.isKanplaAdmin,
    // Legacy login stores a bearer token; Firebase login leaves it null so the
    // API client attaches the ID token instead and the firebase-sync guard
    // treats the session as Firebase-owned.
    token: params.token ?? null
  });
  logger.trace("[AUTH] Login", {
    partner_id: params.partnerId
  });
}

/**
 * Logout user
 */
function logout(): void {
  const currentState = authStore.getState();

  logger.trace("[AUTH] Logout", {
    partner_id: currentState.partnerId,
    user_id: currentState.userId,
    school_id: currentState.schoolId
  });

  authStore.setState(INITIAL_STATE);

  signOut(auth).catch((error: unknown) => {
    logger.warn("[AUTH] Firebase signOut failed", { error: String(error) });
  });
}

/**
 * Set profile data after profile selection
 */
function loginProfile(
  params: Required<Pick<AuthStore, "profileId" | "childId">>
): void {
  setSentryTagProfile(params.profileId);
  authStore.setState({ ...params, status: "profile" });

  logger.trace("[AUTH] Login Profile", {
    child_id: params.childId,
    profile_id: params.profileId
  });
}

/**
 * Logout profile
 */
function logoutProfile(): void {
  const { childId, profileId, userId, schoolId, partnerId } =
    authStore.getState();

  logger.trace("[AUTH] Logout Profile", {
    child_id: childId,
    profile_id: profileId,
    user_id: userId,
    school_id: schoolId,
    partner_id: partnerId
  });

  clearSentryTagProfile();
  authStore.setState({ childId: null, profileId: null, status: "till" });
}

/**
 * Set unattended status for unattended tills (kiosk, self-service, etc.)
 * This restricts access to non-admin pages until profile PIN is verified
 */
function setUnattended(): void {
  const { profileId, userId, schoolId, partnerId } = authStore.getState();

  logger.trace("[AUTH] Set Unattended", {
    profile_id: profileId,
    user_id: userId,
    school_id: schoolId,
    partner_id: partnerId
  });

  authStore.setState({ status: "unattended" });
}

type AssignTillParams = Required<
  Pick<AuthStore, "moduleId" | "userId" | "schoolId" | "tillId" | "partnerId">
>;
/**
 * Set till data after till assignment
 */
function assignTill(params: Omit<AssignTillParams, "partnerId">): void {
  authStore.setState({
    ...params,
    deviceTillId: params.tillId,
    status: "till"
  });
}

/**
 * Set till data to impersonate till (except updating deviceTillId)
 */
function impersonateTill(params: AssignTillParams): void {
  authStore.setState({
    ...params,
    status: "till"
  });
}

function logoutTill(): void {
  const { tillId, userId, schoolId, partnerId } = authStore.getState();

  logger.trace("[AUTH] Logout Till", {
    till_id: tillId,
    user_id: userId,
    school_id: schoolId,
    partner_id: partnerId
  });

  authStore.setState({
    tillId: null,
    moduleId: null,
    status: "user"
  });
}

/**
 * Update online status
 */
function updateOnlineStatus(isOnline: boolean) {
  authStore.setState({
    isOnline
  });
}

const authActions = {
  login,
  logout,
  loginProfile,
  logoutProfile,
  setUnattended,
  assignTill,
  impersonateTill,
  logoutTill,
  updateOnlineStatus
};

export { authActions };
